import { isDefined } from '@sgme/fp';
import { GreeksRow } from 'components/secondaryMarket/productSearch/GreeksRow';
import { CopyAllButtonByExcelGenerator } from 'components/shared/CopyAllButton/CopyAllButton';
import { FC, useCallback, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { WorkspaceAnalyticCategoryContext } from 'shared/middlewares/analytics/loggerContext';
import type { SecondaryMarketProductGreeks } from 'state/secondaryMarket/productSearch';
import { whiteSpaceOnUndefined } from 'utils/string/whiteSpaceOnUndefined';

const useFormatGreeks = (data: SecondaryMarketProductGreeks[], hasSpotDate?: boolean) => {
  const { formatMessage } = useIntl();
  return useCallback(() => {
    const headers = [
      formatMessage({ id: 'secondaryMarket.products.greeks.underlying' }),
      formatMessage({ id: 'secondaryMarket.products.greeks.bloombergTicker' }),
      formatMessage({ id: 'secondaryMarket.products.greeks.spot' }),
      formatMessage({ id: 'secondaryMarket.products.greeks.currency' }),
      formatMessage({ id: 'secondaryMarket.products.greeks.performance' }),
      formatMessage({ id: 'secondaryMarket.products.greeks.underlyingInitialPrice' }),
      formatMessage({ id: 'secondaryMarket.products.greeks.delta' }),
      formatMessage({ id: 'secondaryMarket.products.greeks.gamma' }),
      formatMessage({ id: 'secondaryMarket.products.greeks.theta' }),
      formatMessage({ id: 'secondaryMarket.products.greeks.vega' }),
      formatMessage({ id: 'secondaryMarket.products.greeks.vol' }),
      hasSpotDate ? formatMessage({ id: 'secondaryMarket.products.greeks.spotDate' }) : undefined,
    ].filter(isDefined);
    return [
      headers,
      ...data.map(
        row =>
          [
            whiteSpaceOnUndefined(row.label),
            whiteSpaceOnUndefined(row.bloombergTicker),
            whiteSpaceOnUndefined(row.spot),
            whiteSpaceOnUndefined(row.currency),
            whiteSpaceOnUndefined(row.underlyingInitialPrice !== undefined ? row.performance : undefined),
            whiteSpaceOnUndefined(row.underlyingInitialPrice),
            whiteSpaceOnUndefined(row.delta),
            whiteSpaceOnUndefined(row.gamma),
            whiteSpaceOnUndefined(row.theta),
            whiteSpaceOnUndefined(row.vega),
            whiteSpaceOnUndefined(row.vol),
            whiteSpaceOnUndefined(hasSpotDate ? row.spotDate : undefined),
          ] as string[],
      ),
    ];
  }, [formatMessage, hasSpotDate, data]);
};

export const ProductSearchGreeks: FC<{
  data?: SecondaryMarketProductGreeks[];
  greeksPrecision: number;
  hasSpotDate?: boolean;
  isLoadingGreeks: boolean;
}> = ({ data = [], greeksPrecision, hasSpotDate = false, isLoadingGreeks }) => {
  const formatData = useFormatGreeks(data, hasSpotDate);
  const logAnalyticsCategory = useContext(WorkspaceAnalyticCategoryContext);
  return (
    <div className="d-flex flex-column">
      <table
        style={{ tableLayout: 'fixed', borderStyle: 'hidden' }}
        className="table table-sm table-striped table-bordered"
      >
        <colgroup>
          <col style={{ width: '5vw' }} />
          <col style={{ width: '5vw' }} />
          <col style={{ width: '5vw' }} />
          <col style={{ width: '5vw' }} />
          <col style={{ width: '5vw' }} />
          <col style={{ width: '6vw' }} />
          <col style={{ width: '5vw' }} />
          <col style={{ width: '5vw' }} />
          <col style={{ width: '5vw' }} />
          <col style={{ width: '5vw' }} />
          <col style={{ width: '5vw' }} />
          <col style={{ width: '5vw', borderStyle: hasSpotDate ? 'visible' : 'hidden' }} />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">
              <FormattedMessage id="secondaryMarket.products.greeks.underlying" />
            </th>
            <th scope="col">
              <FormattedMessage id="secondaryMarket.products.greeks.bloombergTicker" />
            </th>
            <th scope="col">
              <FormattedMessage id="secondaryMarket.products.greeks.spot" />
            </th>
            <th scope="col">
              <FormattedMessage id="secondaryMarket.products.greeks.currency" />
            </th>
            <th scope="col">
              <FormattedMessage id="secondaryMarket.products.greeks.performance" />
            </th>
            <th scope="col">
              <FormattedMessage id="secondaryMarket.products.greeks.underlyingInitialPrice" />
            </th>
            <th scope="col">
              <FormattedMessage id="secondaryMarket.products.greeks.delta" />
            </th>
            <th scope="col">
              <FormattedMessage id="secondaryMarket.products.greeks.gamma" />
            </th>
            <th scope="col">
              <FormattedMessage id="secondaryMarket.products.greeks.theta" />
            </th>
            <th scope="col">
              <FormattedMessage id="secondaryMarket.products.greeks.vega" />
            </th>
            <th scope="col">
              <FormattedMessage id="secondaryMarket.products.greeks.vol" />
            </th>
            {hasSpotDate && (
              <th scope="col">
                <FormattedMessage id="secondaryMarket.products.greeks.spotDate" />
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {data.map(d => (
            <GreeksRow
              key={d.label}
              {...d}
              greeksPrecision={greeksPrecision}
              hasSpotDate={hasSpotDate}
              isLoadingGreeks={isLoadingGreeks}
            />
          ))}
        </tbody>
      </table>
      <div className="d-flex justify-content-end">
        <CopyAllButtonByExcelGenerator
          generateExcelGrid={formatData}
          analyticsCategory={logAnalyticsCategory}
          analyticsAction="Copy greek"
        />
      </div>
    </div>
  );
};

ProductSearchGreeks.displayName = 'ProductSearchGreeks';
